import { Core, Decorative } from "@faire/design-tokens";
import { IRetailer } from "@faire/web-api/indigofair/data/IRetailer";
import { Button } from "@faire/web/slate/Button";
import { Insider } from "@faire/web/slate/icons/InsiderIcon";
import { Column, Row } from "@faire/web/slate/Layout";
import { Spacer, getSpacing } from "@faire/web/slate/spacing";
import { FaireMarketTheme } from "@faire/web/slate/Theme/EventsTheme";
import { getEventTheme } from "@faire/web/slate/Theme/eventsThemeColors";
import { EventsThemeShape } from "@faire/web/slate/Theme/EventsThemeShape";
import { EventsThemeShapeCombination } from "@faire/web/slate/Theme/EventsThemeShapeCombination";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { ThemeProvider } from "@faire/web/slate/Theme/ThemeProvider";
import { Typography } from "@faire/web/slate/Typography";
import { ResponsiveContentV2 } from "@faire/web/ui/ResponsiveContentV2";
import React from "react";
import styled from "styled-components";

import { Context } from "@faire/retailer/components/LayoutElementRenderer/__internal__/Context";
import { useInsiderState } from "@faire/retailer/hooks/insider/useInsiderState";
import { RetailerSegment } from "@faire/retailer/stores/domain/Events/FaireMarkets/MarketStore";
import { useMarketStore } from "@faire/retailer/stores/domain/Events/FaireMarkets/MarketStore";
import { useRetailerStore } from "@faire/retailer/stores/domain/RetailerStore";

interface IBannerColors {
  backgroundColor?: string;
  hoverColor?: string;
  textColor?: string;
}

export const HomeFaireMarketPreshowBanner: React.FC = () => {
  const { layoutElement, onCTAClick, requestId } = React.useContext(Context);
  const { retailerSegment, formattedEarlyAccessStartDay } = useMarketStore([
    "retailerSegment",
    "formattedEarlyAccessStartDay",
  ]);
  const { insiderMembershipState } = useRetailerStore([
    "insiderMembershipState",
  ]);
  const { isEurope } = useRetailerStore(["isEurope"]);
  const { isInsiderPaused } = useInsiderState();
  const {
    cta_link,
    cta_text,
    on_click_event_name,
    main_text,
    description_text,
    tagline_text,
  } = layoutElement;
  const onClick = () => {
    onCTAClick?.(
      on_click_event_name ?? "HomeFaireMarketPreshowBanner",
      requestId
    );
  };

  const isInsiderLayout = () => {
    return retailerSegment === RetailerSegment.INSIDER_ENROLLED;
  };

  const colors: IBannerColors = isInsiderLayout()
    ? {
        backgroundColor: Decorative.shade.teal,
        hoverColor: getEventTheme("faireMarket").decorative6,
        textColor: getEventTheme("faireMarket").promoTextInverse,
      }
    : {
        backgroundColor: getEventTheme("faireMarket").primaryDark,
        textColor: getEventTheme("faireMarket").promoTextInverse,
      };

  const preambleDate = () => {
    return formattedEarlyAccessStartDay;
  };

  const renderPreamble = () => {
    if (insiderMembershipState === IRetailer.InsiderMembershipState.UNPAID) {
      return null;
    }
    const preambleFragment = (
      <>
        <Spacer size="2x" />
        <VerticalLine $isInsiderEnrolled={isInsiderLayout()} />
        <Spacer size="2x" />
        {preambleDate()}
      </>
    );
    switch (retailerSegment) {
      case RetailerSegment.INSIDER_ENROLLED:
      case RetailerSegment.INSIDER_ELIGIBLE:
      case RetailerSegment.INSIDER_CHURNED:
        return (
          <>
            <Row>
              <Insider
                height="26px"
                color={getEventTheme("faireMarket").promoTextInverse}
              />
              <Spacer size="1x" />
              <Text
                variant="subheadingSansMedium"
                $isInsiderEnrolled={isInsiderLayout()}
              >
                <Row>
                  {tagline_text}
                  {preambleFragment}
                </Row>
              </Text>
            </Row>
            <Spacer size="2x" />
          </>
        );
      default:
        return null;
    }
  };

  if (isInsiderPaused && isEurope) {
    return null;
  }

  const isInsiderEnrolled =
    retailerSegment === RetailerSegment.INSIDER_ENROLLED;

  return (
    <ThemeProvider theme={FaireMarketTheme}>
      <Wrapper $backgroundColor={colors.backgroundColor}>
        {isInsiderEnrolled ? (
          <>
            <InsiderFaireMarketIcon
              color={getEventTheme("faireMarket").decorative5}
            />
            <StyledMarketSecondaryShape
              color={getEventTheme("faireMarket").primaryLight}
            />
          </>
        ) : (
          <ResponsiveContentV2
            mobile={
              <MobileFaireMarketIcon
                color={getEventTheme("faireMarket").badgeIcon}
                $isNotEligible={retailerSegment === RetailerSegment.NOT_INSIDER}
              />
            }
            tabletAndAbove={
              <StyledMarketCombinationShape
                width="446px"
                height="281px"
                frontShapeColor={getEventTheme("faireMarket").decorative6 ?? ""}
                backShapeColor={getEventTheme("faireMarket").decorative5 ?? ""}
              />
            }
          />
        )}
        <Content>
          {renderPreamble()}
          <Title
            variant={{
              tabletAndAbove: "displayMSerifRegular",
              mobileAndAbove: "displaySSerifRegular",
            }}
            $isInsiderEnrolled={isInsiderLayout()}
          >
            {main_text}
          </Title>
          <Spacer size="2x" />
          <Subtitle
            variant={{
              tabletAndAbove: "subheadingSansRegular",
              mobileAndAbove: "paragraphSansRegular",
            }}
            $isInsiderEnrolled={isInsiderLayout()}
          >
            {description_text}
          </Subtitle>
          <Spacer size="3x" />
          {cta_text && cta_link ? (
            <StyledCTA
              linkTo={cta_link}
              $hoverColor={colors.hoverColor}
              $textColor={colors.textColor}
              onClick={onClick}
            >
              {cta_text}
            </StyledCTA>
          ) : null}
        </Content>
      </Wrapper>
    </ThemeProvider>
  );
};

const Wrapper = styled.div<{
  $backgroundColor?: string;
}>`
  background-color: ${({ $backgroundColor }) =>
    $backgroundColor ? $backgroundColor : null};
  position: relative;
  padding: ${getSpacing("7x", "8x")};
  overflow: hidden;
  max-width: 1280px;
  margin: 0 auto;
  border-radius: ${getSpacing("1x")};

  ${responsiveMediaQueries.tablet} {
    padding: ${getSpacing("7x", "8x")};
    margin: ${getSpacing(0, "7x")};
  }

  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing("4x", "3x")};
    border-radius: 0;
  }
`;

const StyledMarketCombinationShape = styled(
  EventsThemeShapeCombination.faireMarket.comboB
)`
  position: absolute;
  bottom: ${getSpacing("-6x")};
  right: ${getSpacing("-10x")};
`;

const FaireMarketIcon = styled(EventsThemeShape.faireMarket.badge)`
  position: absolute;
  height: auto;
`;

const InsiderFaireMarketIcon = styled(FaireMarketIcon).attrs({
  color: getEventTheme("faireMarket").decorative4,
})`
  right: 98px;
  top: 41px;
  width: 96px;

  ${responsiveMediaQueries.mobile} {
    display: none;
  }
`;

const MobileFaireMarketIcon = styled(FaireMarketIcon).attrs({
  width: "88px",
})<{ $isNotEligible?: boolean }>`
  position: absolute;
  right: 0px;
  top: ${({ $isNotEligible }) => ($isNotEligible ? "-18px" : "24px")};
`;

const StyledMarketSecondaryShape = styled(
  EventsThemeShape.faireMarket.secondary
).attrs({
  color: getEventTheme("faireMarket").decorative2,
})`
  position: absolute;
  width: 139px;
  height: 122px;
  top: ${getSpacing("-2x")};
  right: -35px;

  ${responsiveMediaQueries.tabletAndAbove} {
    width: 224px;
    height: 197px;
    top: unset;
    bottom: -7px;
    right: ${getSpacing("-6x")};
  }
`;

const Content = styled(Column).attrs({
  align: "start",
})`
  position: relative;
  ${responsiveMediaQueries.desktopAndAbove} {
    max-width: calc(100% - 130px);
  }

  ${responsiveMediaQueries.tablet} {
    max-width: calc(100% - 179px);
  }
`;

const Text = styled(Typography)<{ $isInsiderEnrolled?: boolean }>`
  &&& {
    /* For FSM'24 both colours are the same. */
    color: ${({ $isInsiderEnrolled }) =>
      $isInsiderEnrolled
        ? getEventTheme("faireMarket").promoTextInverse
        : getEventTheme("faireMarket").promoTextInverse};
  }
`;

const Title = styled(Text)`
  &&& {
    ${responsiveMediaQueries.mobile} {
      padding-right: ${getSpacing("10x")};
    }
    width: 100%;
  }
`;

const Subtitle = styled(Text)<{
  $isInsiderEnrolled: boolean;
}>`
  max-width: 730px;

  ${responsiveMediaQueries.tablet} {
    max-width: 434px;
  }

  ${responsiveMediaQueries.mobile} {
    max-width: 100%;
  }
`;

const StyledCTA = styled(Button)<{
  $hoverColor?: string;
  $textColor?: string;
}>`
  background: ${Core.surface.inverse};
  color: ${Core.text.default};
  padding: ${getSpacing("3x")};
  min-width: 200px;

  &:hover,
  &:focus {
    background-color: ${(props) => props.$hoverColor};
    color: ${(props) => props.$textColor};
  }

  ${responsiveMediaQueries.mobile} {
    width: 100%;
  }
`;

const VerticalLine = styled.div<{ $isInsiderEnrolled: boolean }>`
  height: 26px;
  /* For FSM'24 both colours are the same. */
  border-right: 1px solid
    ${({ $isInsiderEnrolled }) =>
      $isInsiderEnrolled
        ? getEventTheme("faireMarket").promoTextInverse
        : getEventTheme("faireMarket").promoTextInverse};
`;
